/**
 * The prototype's own colours, and what it means to change one (Prototype Editor UX plan, step 4).
 *
 * A prototype carries a design system Chef wrote from the spec: `src/index.css` holds the tokens
 * (`--primary`, `--muted-foreground`, `--radius`) and the components use them through classes
 * (`bg-primary`, `text-muted-foreground`). So a founder picking a custom colour on a button that
 * uses `bg-primary` is asking one of two different things, and the editor has to ask which:
 * change this button, or change Primary everywhere it is used.
 *
 * Text in, text out: the rules are tested without a browser.
 */

/** The shadcn token a class names, for one property: `bg-primary` -> `primary`. */
export function tokenInClasses(className: string, prefix: 'bg' | 'text' | 'border'): string | null {
  const known = new Set<string>(KNOWN_TOKENS);
  for (const cls of className.split(/\s+/)) {
    if (!cls.startsWith(`${prefix}-`)) continue;
    const token = cls.slice(prefix.length + 1);
    if (known.has(token)) return token;
  }
  return null;
}

const KNOWN_TOKENS = [
  'primary',
  'primary-foreground',
  'secondary',
  'secondary-foreground',
  'accent',
  'accent-foreground',
  'muted',
  'muted-foreground',
  'destructive',
  'destructive-foreground',
  'card',
  'card-foreground',
  'background',
  'foreground',
  'border',
  'input',
  'ring',
] as const;

/** "Primary", "Muted text": the token in the founder's words. */
export function tokenLabel(token: string): string {
  const words = token.replace('-foreground', ' text').split('-').join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** `#0f766e` -> `175.3 77.4% 26.1%`, the shape the prototype's CSS variables are written in. */
export function hexToHslParts(hex: string): string | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const raw = m[1]!;
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const round = (n: number) => Math.round(n * 10) / 10;
  return `${round(h)} ${round(s * 100)}% ${round(l * 100)}%`;
}

/**
 * The stylesheet with one token set to `value`, in the light theme only: the dark theme is its
 * own set of colours, and changing a colour in daylight must not repaint the night one.
 */
export function setTokenInCss(css: string, token: string, value: string): string | null {
  const rootAt = css.indexOf(':root');
  if (rootAt < 0) return null;
  // The dark theme redefines the same names further down; stop before it.
  const darkAt = css.indexOf('.dark', rootAt);
  const end = darkAt < 0 ? css.length : darkAt;
  const pattern = new RegExp(`(--${token}\\s*:\\s*)([^;]+)(;)`);
  const head = css.slice(0, end);
  if (!pattern.test(head)) return null;
  return head.replace(pattern, `$1${value}$3`) + css.slice(end);
}

/** How many places in the prototype use this token, so the prompt can say it. */
export function countTokenUses(files: Record<string, string>, token: string): number {
  // `-foreground` is its own token, so `text-primary-foreground` is not a use of `primary`.
  const pattern = new RegExp(`\\b(?:bg|text|border|ring|from|to|via)-${token}(?![-\\w])`, 'g');
  let count = 0;
  for (const [path, source] of Object.entries(files)) {
    if (!/^src\/.+\.(tsx|jsx)$/.test(path.replace(/\\/g, '/'))) continue;
    count += (source.match(pattern) ?? []).length;
  }
  return count;
}

/** What the prompt says when a custom colour lands on an element that uses a token. */
export function tokenPromptLines(token: string, uses: number): { title: string; body: string } {
  const label = tokenLabel(token);
  const others = Math.max(uses - 1, 0);
  return {
    title: `Make this the new ${label}?`,
    body:
      others > 0
        ? `This colour comes from the prototype's design system. ${label} is used in ${others} other ${
            others === 1 ? 'place' : 'places'
          }.`
        : `This colour comes from the prototype's design system, where it is called ${label}.`,
  };
}

// ── Corner radius ─────────────────────────────────────────────────────────────

/**
 * The corner styles the spec knows, written as the stylesheet's `--radius` is (the same values
 * chef's `themeTokenizer` writes, so a read-back lands on the same step). `rounded-lg`, `-md`
 * and `-sm` in the prototype all follow this one variable.
 */
export const RADIUS_STEPS = [
  { step: 'none', label: 'Square', value: '0rem' },
  { step: 'sm', label: 'Slight', value: '0.125rem' },
  { step: 'md', label: 'Soft', value: '0.375rem' },
  { step: 'lg', label: 'Round', value: '0.5rem' },
  { step: 'full', label: 'Pill', value: '9999px' },
] as const;

export type RadiusStep = (typeof RADIUS_STEPS)[number]['step'];

/** Whether the element's corners come from the design system's `--radius`. */
export function usesRadiusToken(className: string): boolean {
  return className.split(/\s+/).some((cls) => /^rounded-(sm|md|lg)$/.test(cls));
}

/** The step nearest a measured radius, so the control can show where the element stands. */
export function radiusStepFor(px: number): RadiusStep {
  if (px >= 999) return 'full';
  if (px >= 7) return 'lg';
  if (px >= 4) return 'md';
  if (px >= 1) return 'sm';
  return 'none';
}
